import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import dayjs from "dayjs";
import { Select, Skeleton } from "antd";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import ChartTooltip from "./ChartTooltip";
import CurrencyFacets from "./CurrencyFacets";
import useCategoryTrend from "../../hooks/useCategoryTrend";
import { CurrencyChip, FacetHeader, FacetWrapper } from "./layout";
import { STATUS_GOOD, STATUS_CRITICAL, TEXT_MUTED, GRIDLINE } from "./palette";
import { formatAmount, formatCompactAmount } from "./format";
import { buildMonthKeys, buildMonthKeysFromRange } from "./monthKeys";

const EmptyNote = styled.div`
  color: ${TEXT_MUTED};
  margin-top: 0.5em;
`;

function TrendTooltip({ active, payload, label, barColor }) {
  if (!active || !payload || payload.length === 0) {
    return null;
  }

  return (
    <ChartTooltip
      label={dayjs(`${label}-01`).format("YYYY年M月")}
      value={formatAmount(payload[0].payload.total)}
      detail={barColor === STATUS_GOOD ? "收入" : "支出"}
    />
  );
}

TrendTooltip.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.arrayOf(PropTypes.object),
  label: PropTypes.string,
  barColor: PropTypes.string.isRequired,
};

TrendTooltip.defaultProps = {
  active: false,
  payload: [],
  label: "",
};

function CurrencyTrendChart({ currencyName, months, rows, barColor, onMonthClick }) {
  const data = useMemo(() => {
    const byMonth = {};
    rows.forEach((row) => {
      byMonth[row.month] = row.total;
    });

    return months.map((month) => ({ month, total: byMonth[month] || 0 }));
  }, [months, rows]);

  function handleClick(clickData) {
    const entry = clickData && clickData.payload ? clickData.payload : clickData;
    if (!onMonthClick || !entry || !entry.month) {
      return;
    }
    onMonthClick(entry.month);
  }

  return (
    <FacetWrapper>
      <FacetHeader>
        <CurrencyChip>{currencyName}</CurrencyChip>
      </FacetHeader>
      <ResponsiveContainer width="100%" height={200}>
        <BarChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
          <CartesianGrid vertical={false} stroke={GRIDLINE} />
          <XAxis
            dataKey="month"
            tickFormatter={(month) => dayjs(`${month}-01`).format("M月")}
            tick={{ fill: TEXT_MUTED, fontSize: 11 }}
            axisLine={{ stroke: GRIDLINE }}
            tickLine={false}
          />
          <YAxis
            tickFormatter={formatCompactAmount}
            tick={{ fill: TEXT_MUTED, fontSize: 11 }}
            axisLine={false}
            tickLine={false}
            width={48}
          />
          <Tooltip
            content={<TrendTooltip barColor={barColor} />}
            cursor={{ fill: "rgba(11,11,11,0.04)" }}
          />
          <Bar
            dataKey="total"
            fill={barColor}
            radius={[4, 4, 0, 0]}
            maxBarSize={28}
            onClick={handleClick}
            cursor={onMonthClick ? "pointer" : "default"}
          />
        </BarChart>
      </ResponsiveContainer>
    </FacetWrapper>
  );
}

CurrencyTrendChart.propTypes = {
  currencyName: PropTypes.string.isRequired,
  months: PropTypes.arrayOf(PropTypes.string).isRequired,
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  barColor: PropTypes.string.isRequired,
  onMonthClick: PropTypes.func,
};

CurrencyTrendChart.defaultProps = {
  onMonthClick: null,
};

// 點擊趨勢圖某月長條時，組出該月的明細篩選條件（僅含該月，若頁面層有自訂範圍則取交集，
// 確保明細總和跟長條金額對得上）。純函式方便單獨測試，不依賴 Apollo/元件狀態。
export function buildMonthDrillDownFilter({ category, currency, month, startDate, endDate }) {
  const monthStart = dayjs(`${month}-01`);
  const monthStartDate = monthStart.format("YYYY-MM-DD");
  const monthEndDate = monthStart.endOf("month").format("YYYY-MM-DD");

  const clampedStart = startDate && startDate > monthStartDate ? startDate : monthStartDate;
  const clampedEnd = endDate && endDate < monthEndDate ? endDate : monthEndDate;

  return {
    kind: "category",
    categoryId: category.id,
    categoryType: category.type,
    currencyId: currency.id,
    currencyName: currency.name,
    label: category.name,
    startDate: clampedStart,
    endDate: clampedEnd,
    rangeLabel: monthStart.format("YYYY年M月"),
  };
}

// 分類選單只列出所選區間內有資料的分類，取自已載入的 categorySpending（不用額外查詢）。
// 純函式，方便直接單元測試去重邏輯，不需要渲染整個元件。
export function dedupeCategories(categorySpending) {
  const byId = new Map();
  (categorySpending || []).forEach((row) => {
    if (!byId.has(row.category.id)) {
      byId.set(row.category.id, row.category);
    }
  });
  return Array.from(byId.values());
}

// 純呈現元件：不呼叫 Apollo hook，接收已查詢好的資料，方便不依賴 Apollo context 就能測試。
export function CategoryTrendBody({
  categoryId,
  categoryTrend,
  loading,
  monthKeys,
  barColor,
  onMonthClick,
}) {
  if (!categoryId) {
    return <EmptyNote>選擇分類後可查看逐月金額變化。</EmptyNote>;
  }

  if (loading && !categoryTrend) {
    return <Skeleton active title={false} paragraph={{ rows: 4 }} style={{ marginTop: "1em" }} />;
  }

  if (!categoryTrend || categoryTrend.length === 0) {
    return <EmptyNote>此分類在所選區間內沒有資料。</EmptyNote>;
  }

  return (
    <CurrencyFacets rows={categoryTrend}>
      {(currency, rows) => (
        <CurrencyTrendChart
          key={currency.id}
          currencyName={currency.name}
          months={monthKeys}
          rows={rows}
          barColor={barColor}
          onMonthClick={onMonthClick ? (month) => onMonthClick(currency, month) : null}
        />
      )}
    </CurrencyFacets>
  );
}

CategoryTrendBody.propTypes = {
  categoryId: PropTypes.string,
  categoryTrend: PropTypes.arrayOf(PropTypes.object),
  loading: PropTypes.bool,
  monthKeys: PropTypes.arrayOf(PropTypes.string).isRequired,
  barColor: PropTypes.string.isRequired,
  onMonthClick: PropTypes.func,
};

CategoryTrendBody.defaultProps = {
  categoryId: undefined,
  categoryTrend: null,
  loading: false,
  onMonthClick: null,
};

export default function CategoryTrendChart({
  categorySpending,
  months,
  startDate,
  endDate,
  onSelect,
}) {
  const [categoryId, setCategoryId] = useState(undefined);
  const options = useMemo(() => dedupeCategories(categorySpending), [categorySpending]);
  const selectedCategory = options.find((option) => option.id === categoryId);

  const { loading, categoryTrend } = useCategoryTrend({ categoryId, months, startDate, endDate });

  const monthKeys = useMemo(
    () =>
      startDate && endDate ? buildMonthKeysFromRange(startDate, endDate) : buildMonthKeys(months),
    [months, startDate, endDate]
  );

  const barColor =
    selectedCategory && selectedCategory.type === "income" ? STATUS_GOOD : STATUS_CRITICAL;

  function handleMonthClick(currency, month) {
    if (!onSelect || !selectedCategory) {
      return;
    }
    onSelect(
      buildMonthDrillDownFilter({ category: selectedCategory, currency, month, startDate, endDate })
    );
  }

  return (
    <>
      <Select
        allowClear
        placeholder="請選擇分類"
        value={categoryId}
        onChange={setCategoryId}
        style={{ width: 200 }}
      >
        {options.map((category) => (
          <Select.Option key={category.id} value={category.id}>
            {category.name}
          </Select.Option>
        ))}
      </Select>

      <CategoryTrendBody
        categoryId={categoryId}
        categoryTrend={categoryTrend}
        loading={loading}
        monthKeys={monthKeys}
        barColor={barColor}
        onMonthClick={onSelect ? handleMonthClick : null}
      />
    </>
  );
}

CategoryTrendChart.propTypes = {
  categorySpending: PropTypes.arrayOf(PropTypes.object),
  months: PropTypes.number,
  startDate: PropTypes.string,
  endDate: PropTypes.string,
  onSelect: PropTypes.func,
};

CategoryTrendChart.defaultProps = {
  categorySpending: null,
  months: undefined,
  startDate: undefined,
  endDate: undefined,
  onSelect: null,
};
